import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { auth } from "../../firebase";
import config from '../../config';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  TextField,
  Button,
  Stack,
  Chip
} from '@mui/material';
import {
  Info as InfoIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Article as ArticleIcon,
  Route as RouteIcon,
  Assessment as AssessmentIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Tooltip descriptions for metrics
const metricDescriptions = {
  sessionDuration: "The average length of time users spend on the site during a single session. A longer duration often indicates more engaged users.",
  pagesPerSession: "The average number of pages viewed during a single session. Higher numbers suggest users are exploring more content.",
  engagementRate: "The percentage of sessions that lasted longer than 10 seconds, had a conversion event, or had 2+ page views. Higher is better.",
  bounceRate: "The percentage of sessions where users left after viewing only one page. Lower rates are generally better.",
};

// Helper function to get human-readable page titles from routes
const getPageTitle = (path) => {
  const pathMap = {
    '/': 'Home',
    '/dashboard': 'Dashboard',
    '/district-analysis': 'District Analysis',
    '/school-summary': 'School Summary',
    '/geographic-explorer': 'Geographic Explorer',
    '/competitive-explorer': 'Competitive Explorer',
    '/projections-explorer': 'Projections Explorer',
    '/student-catchment-explorer': 'Student Catchment Explorer',
    '/admin': 'Admin',
    '/admin/user-management': 'User Management',
    '/admin/team-management': 'Team Management',
    '/admin/team-groups-management': 'Team Groups',
    '/admin/analytics': 'Analytics Dashboard',
    '/admin/academic-data': 'Academic Data',
    '/admin/feedback': 'Feedback',
    '/admin/schools-validation': 'Schools Validation',
    '/admin/school-processor': 'School Processor',
    '/admin/tx-district-tool': 'TX District Tool',
    '/city-explorer': 'City Explorer',
    '/admin/ai-competitive-analysis': 'AI Competitive Analysis',
    '/admin/ai-summary': 'AI Summary',
    '/admin/public-links': 'Public Links',
    '/admin/drop-a-pin': 'Drop-a-Pin Dashboard',
    '/admin/drop-a-pin-projections': 'Drop-a-Pin Projections Explorer',
    '/preferences': 'Preferences',
    '/resources': 'Resources',
    '/resources/tutorials': 'Video Tutorials',
    '/resources/methodology': 'Methodology',
    '/support': 'Support',
    '/login': 'Login',
    '/terms': 'Terms of Service',
    '/privacy': 'Privacy Notice',
    '/about': 'About Us',
    '/contact': 'Contact',
    '/product': 'Product',
  };
  
  // Check for exact match
  if (pathMap[path]) {
    return pathMap[path];
  }
  
  // Check for dynamic routes (e.g., /school-summary/123)
  if (path.startsWith('/school-summary/')) return 'School Summary';
  if (path.startsWith('/district-analysis/')) return 'District Analysis';
  if (path.startsWith('/geographic-explorer/')) return 'Geographic Explorer';
  if (path.startsWith('/competitive-explorer/')) return 'Competitive Explorer';
  if (path.startsWith('/projections-explorer/')) return 'Projections Explorer';
  if (path.startsWith('/student-catchment-explorer/')) return 'Student Catchment Explorer';
  
  // Default: capitalize and remove hyphens
  return path
    .split('/')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).replace(/-/g, ' '))
    .join(' / ');
};

const MetricCard = ({ title, value, description, icon: IconComponent }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              backgroundColor: 'primary.light',
              borderRadius: '50%',
              p: 1,
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <IconComponent sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography color="textSecondary" variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
          </Box>
          <Tooltip title={description} arrow placement="top">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

const PageAnalyticsTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'views', direction: 'desc' });

  const sortedData = [...data].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              Page
              <Tooltip title="Page title and URL path" arrow>
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
            <TableCell 
              align="right" 
              onClick={() => handleSort('views')}
              sx={{ cursor: 'pointer' }}
            >
              Views
            </TableCell>
            <TableCell 
              align="right"
              onClick={() => handleSort('averageTime')}
              sx={{ cursor: 'pointer' }}
            >
              Avg Time
            </TableCell>
            <TableCell 
              align="right"
              onClick={() => handleSort('bounceRate')}
              sx={{ cursor: 'pointer' }}
            >
              Bounce Rate
              <Tooltip title={metricDescriptions.bounceRate} arrow>
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((page) => (
            <TableRow 
              key={page.path}
              sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
            >
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {getPageTitle(page.path)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {page.path}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="right">{page.views.toLocaleString()}</TableCell>
              <TableCell align="right">
                {`${Math.round(page.averageTime / 60)} min`}
              </TableCell>
              <TableCell align="right">
                {`${(page.bounceRate * 100).toFixed(1)}%`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
    {value === index && children}
  </Box>
);

const AnalyticsPanel = ({ hideTitle = false }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();

  // Date range state - default to last 30 days
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  });

  const [analyticsData, setAnalyticsData] = useState({
    overallMetrics: {
      averageSessionDuration: 0,
      pagesPerSession: 0,
      engagementRate: 0
    },
    pageAnalytics: [],
    timeSeriesData: [] // Added for proper time-series charts
  });

  const fetchAnalyticsData = async () => {
    if (!user) {
      setError('No authenticated user');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Get token from Firebase auth
      const token = await auth.currentUser.getIdToken();
      
      const response = await fetch(
        `${config.apiUrl}/admin/analytics/users?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error(`Analytics failed: ${response.status}`);
      }

      const data = await response.json();
      setAnalyticsData(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const setQuickDateRange = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  };

  useEffect(() => {
    fetchAnalyticsData();
    // Auto-refresh every 5 minutes
    const refreshInterval = setInterval(fetchAnalyticsData, 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dateRange]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
      <CircularProgress />
    </Box>
  );
  
  if (error) return (
    <Alert 
      severity="error" 
      sx={{ mb: 2 }}
      action={
        <IconButton size="small" color="inherit" aria-label="info">
          <InfoIcon fontSize="small" />
        </IconButton>
      }
    >
      Error loading analytics: {error}
    </Alert>
  );

  return (
    <Box>
      {!hideTitle && (
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Analytics Dashboard
        </Typography>
      )}

      {/* Environment & GA Tracking Info */}
      <Card sx={{ mb: 2, bgcolor: 'info.light', borderLeft: 4, borderColor: 'info.main' }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="info" fontSize="small" />
                <Typography variant="body2" fontWeight={500}>
                  Environment:
                </Typography>
                <Chip 
                  label={config.environment.toUpperCase()} 
                  size="small" 
                  color={
                    config.environment === 'production' ? 'success' : 
                    config.environment === 'staging' ? 'warning' : 
                    'info'
                  }
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  GA Measurement ID:
                </Typography>
                <Chip 
                  label={config.gaMeasurementId} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Date Range Selector */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <CalendarIcon color="action" />
            <Typography variant="subtitle1" sx={{ minWidth: '100px' }}>
              Date Range:
            </Typography>
            <TextField
              label="Start Date"
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: '150px' }}
            />
            <TextField
              label="End Date"
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: '150px' }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAnalyticsData}
              size="small"
            >
              Refresh
            </Button>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label="Last 7 days" 
                onClick={() => setQuickDateRange(7)}
                size="small"
                variant="outlined"
                clickable
              />
              <Chip 
                label="Last 30 days" 
                onClick={() => setQuickDateRange(30)}
                size="small"
                variant="outlined"
                clickable
              />
              <Chip 
                label="Last 90 days" 
                onClick={() => setQuickDateRange(90)}
                size="small"
                variant="outlined"
                clickable
              />
              <Chip 
                label="Last Year" 
                onClick={() => setQuickDateRange(365)}
                size="small"
                variant="outlined"
                clickable
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Session Duration"
            value={`${Math.round(analyticsData.overallMetrics.averageSessionDuration / 60)} min`}
            description={metricDescriptions.sessionDuration}
            icon={AccessTimeIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Pages per Session"
            value={analyticsData.overallMetrics.pagesPerSession.toFixed(1)}
            description={metricDescriptions.pagesPerSession}
            icon={ArticleIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Engagement Rate"
            value={`${(analyticsData.overallMetrics.engagementRate * 100).toFixed(1)}%`}
            description={metricDescriptions.engagementRate}
            icon={PeopleIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Trending Pages"
            value={analyticsData.pageAnalytics.length}
            description="Number of active pages being tracked"
            icon={TrendingUpIcon}
          />
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          aria-label="analytics tabs"
        >
          <Tab icon={<AssessmentIcon />} label="Page Analytics" />
          <Tab icon={<TrendingUpIcon />} label="Engagement Trends" />
          <Tab icon={<RouteIcon />} label="User Flow" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <PageAnalyticsTable data={analyticsData.pageAnalytics} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* Active Users Over Time */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Active Users Over Time
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Daily count of unique users who engaged with the platform
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsData.timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="activeUsers" 
                      stroke={theme.palette.primary.main} 
                      name="Active Users"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Sessions & Page Views */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sessions & Page Views
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Total visits and pages viewed each day - higher indicates more engagement
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsData.timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="sessions" 
                      stroke={theme.palette.secondary.main} 
                      name="Sessions"
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="pageViews" 
                      stroke={theme.palette.primary.main} 
                      name="Page Views"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Engagement Rate Over Time */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Engagement Rate Over Time
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Percentage of sessions with meaningful interaction (10+ seconds or 2+ pages)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsData.timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                    />
                    <RechartsTooltip 
                      formatter={(value) => `${(value * 100).toFixed(1)}%`}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="engagementRate" 
                      stroke="#00C49F" 
                      name="Engagement Rate"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Average Session Duration */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Average Session Duration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  How long users typically spend on the platform per visit
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsData.timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      tickFormatter={(value) => `${Math.round(value / 60)}m`}
                    />
                    <RechartsTooltip 
                      formatter={(value) => `${Math.round(value / 60)} minutes`}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="avgSessionDuration" 
                      stroke="#FF8042" 
                      name="Avg Duration (sec)"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {/* Top Pages by Views */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Pages by Views
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Most visited pages - shows which features attract the most traffic
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart 
                    data={analyticsData.pageAnalytics
                      .sort((a, b) => b.views - a.views)
                      .slice(0, 10)
                      .map(page => ({
                        ...page,
                        displayName: getPageTitle(page.path)
                      }))}
                    layout="vertical"
                    margin={{ left: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="displayName" 
                      tick={{ fontSize: 11 }}
                      width={180}
                    />
                    <RechartsTooltip 
                      formatter={(value, name, props) => [
                        value.toLocaleString() + ' views',
                        props.payload.path
                      ]}
                      labelFormatter={(label) => label}
                    />
                    <Bar 
                      dataKey="views" 
                      fill={theme.palette.primary.main}
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Engagement Distribution */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Session Engagement Distribution
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Breakdown of engaged sessions vs. quick visits - aim for higher engagement
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={[
                        { 
                          name: 'Engaged Sessions', 
                          value: analyticsData.overallMetrics.engagementRate * 100,
                          description: '10+ seconds or 2+ page views'
                        },
                        { 
                          name: 'Quick Visits', 
                          value: (1 - analyticsData.overallMetrics.engagementRate) * 100,
                          description: 'Less than 10 seconds'
                        }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                    >
                      <Cell fill="#00C49F" />
                      <Cell fill="#FF8042" />
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value) => `${value.toFixed(1)}%`}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Pages with Highest Bounce Rate */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pages with Highest Bounce Rate
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pages where users leave without interaction - lower is better
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart 
                    data={analyticsData.pageAnalytics
                      .filter(page => page.views > 10) // Only show pages with meaningful traffic
                      .sort((a, b) => b.bounceRate - a.bounceRate)
                      .slice(0, 10)
                      .map(page => ({
                        ...page,
                        displayName: getPageTitle(page.path)
                      }))}
                    layout="vertical"
                    margin={{ left: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="displayName" 
                      tick={{ fontSize: 11 }}
                      width={180}
                    />
                    <RechartsTooltip 
                      formatter={(value, name, props) => [
                        `${(value * 100).toFixed(1)}%`,
                        props.payload.path
                      ]}
                      labelFormatter={(label) => label}
                    />
                    <Bar 
                      dataKey="bounceRate" 
                      fill="#FF8042"
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Average Time on Pages */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Average Time on Top Pages
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pages where users spend the most time - indicates valuable content
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart 
                    data={analyticsData.pageAnalytics
                      .sort((a, b) => b.averageTime - a.averageTime)
                      .slice(0, 10)
                      .map(page => ({
                        ...page,
                        displayName: getPageTitle(page.path)
                      }))}
                    layout="vertical"
                    margin={{ left: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number"
                      tickFormatter={(value) => `${Math.round(value / 60)}m`}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="displayName" 
                      tick={{ fontSize: 11 }}
                      width={180}
                    />
                    <RechartsTooltip 
                      formatter={(value, name, props) => [
                        `${Math.round(value / 60)} minutes`,
                        props.payload.path
                      ]}
                      labelFormatter={(label) => label}
                    />
                    <Bar 
                      dataKey="averageTime" 
                      fill="#8884d8"
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default AnalyticsPanel;